import type { CalendarView } from 'angular-calendar';
import * as z from 'zod';

import type { ProjectId } from '../../types';
import { ExportLayout } from '../../types';
import type { SearchItemFilterType } from '../../../search/models/search';
import type { IRecentlyViewedStorageItem } from '../../../shared/models';
import { brandedId } from '../../../shared/utils/branded.util';
import type { UserId, UserTableColumnOrderState, User } from '../../../user/models/user';
import type { FilteredTableId, LariDiffResult } from '../../api/schema';
import { AttachmentParamType, LocalStorageItem, NO_FILTER } from '../../constants';
import { IssueDetailTab } from '../../enums/issue-detail-tab.enum';
import type {
  ContentLanguageItem,
  Filter,
  ICalendarExtraFilters,
  IDateRange,
  IDateRangeValue,
  IInboxExtraFilters,
} from '../../models/common.models';

/** Shape persisted by the date-range filters (list + web search). */
export interface IStoredDateRangeFilter {
  range: IDateRange;
  customValues?: IDateRangeValue | undefined;
}

/**
 * Typed schema for the browser localStorage the app persists (#4698).
 *
 * Each entry binds a friendly key to (a) the physical {@link LocalStorageItem},
 * (b) a {@link StorageCodec} owning serialization, and (c) whether the value is
 * global (shared across users) or namespaced to the current user.
 *
 * `read` receives the value AFTER `LocalStorageService` parsing (which does a
 * best-effort `JSON.parse`, falling back to the raw string), so a codec only has
 * to coerce/validate that value — never re-parse it. `write` returns the string
 * to persist.
 *
 * Codecs are total by default: `read` returns a usable value (falling back to a
 * default) rather than throw, so a stale or hand-edited entry degrades the feature
 * to its default instead of crashing app boot. `user` is the deliberate exception —
 * see {@link jsonRequired}.
 */
export interface StorageCodec<R, W = R> {
  read(parsed: unknown): R;
  write(value: W): string;
}

export interface StorageEntry<R, W = R> {
  item: LocalStorageItem;
  codec: StorageCodec<R, W>;
  /** Shared across users (not namespaced by user id). Defaults to false. */
  isGlobal?: boolean;
}

// --- codec factories -------------------------------------------------------

/** Storage values arrive as whatever `JSON.parse` made of them ('1' -> 1, 'true' -> true). */
const asString = (parsed: unknown): string => {
  if (typeof parsed === 'number' || typeof parsed === 'boolean') {
    return parsed ? String(parsed) : '';
  }
  return typeof parsed === 'string' ? parsed : '';
};

/** Plain string, stored raw. Non-string values coerce via {@link asString}. */
const str = (): StorageCodec<string> => ({
  read: asString,
  write: value => value,
});

/** Nullable string, stored raw; a missing (or falsy) entry reads back as null. */
const strOrNull = (): StorageCodec<string | null> => ({
  read: parsed => asString(parsed) || null,
  write: value => value ?? '',
});

/**
 * Sort-option-style key: reads as string (empty -> null), used where callers
 * distinguish "never set" from a real value.
 */
const strEmptyAsNull = (): StorageCodec<string | null> => ({
  read: parsed => {
    if (parsed === '' || parsed === null || parsed === undefined) {
      return null;
    }
    return asString(parsed);
  },
  write: value => value ?? '',
});

/** Integer stored raw; reads back as number or null when absent. */
const intOrNull = (): StorageCodec<number | null, number> => ({
  read: parsed => {
    const s = asString(parsed);
    return s ? parseInt(s) : null;
  },
  write: value => String(value),
});

/**
 * Legacy "coerce anything to a string" keys: their setters were untyped, so the
 * write side stays `unknown`. Both directions go through {@link asString}.
 */
const coerce = (): StorageCodec<string, unknown> => ({
  read: asString,
  write: asString,
});

/**
 * The one place a stored value becomes a declared type. `schema` guards the persisted
 * shape (not necessarily the full `R` — branded/enum leaf types are asserted) and
 * `safeParse` never throws, so corrupt or stale data reaches `fallback()`. Object
 * schemas should be `loose` where the stored value carries fields beyond those
 * validated, so passthrough keys survive the round-trip.
 */
const parseOr = <R>(schema: z.ZodType, parsed: unknown, fallback: () => R): R => {
  const result = schema.safeParse(parsed);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the schema is a structural guard; `R` is the key’s declared contract
  return result.success ? (result.data as R) : fallback();
};

/** Runtime-validated JSON: a stale or hand-edited entry degrades the feature to `fallback()`. */
const zodCodec = <R>(schema: z.ZodType, fallback: () => R): StorageCodec<R> => ({
  read: parsed => parseOr(schema, parsed, fallback),
  write: value => JSON.stringify(value),
});

/** JSON whose absence is a bug, not a default. Callers that can legitimately run before it is written must catch. */
const zodRequired = <R>(schema: z.ZodType, name: string): StorageCodec<R> => ({
  read: parsed =>
    parseOr<R>(schema, parsed, () => {
      throw new Error(`localStorage entry "${name}" is ${parsed ? 'malformed' : 'not set'}`);
    }),
  write: value => JSON.stringify(value),
});

/**
 * Like {@link coerce} on write, but the read side validates instead of coercing, so a
 * value written by an older release (a since-removed enum member) reads back as null.
 */
const zodCoerceAs = <R>(schema: z.ZodType): StorageCodec<R | null, unknown> => ({
  read: parsed => parseOr<R | null>(schema, parsed, () => null),
  write: asString,
});

// Validation schemas for the structured keys. Kept intentionally permissive: the
// goal is to reject non-conforming garbage (degrading to a default), not to
// re-derive every field of the domain model.
const exportSettingsSchema = z.object({
  layout: z.enum(ExportLayout),
  export_attachments: z.boolean(),
  export_lari_items: z.boolean(),
});
// `ContentLanguage` has no empty member, so a blank `value` is pre-`ANY_LANGUAGE` data.
const languageFilterSchema = z.looseObject({ display: z.string(), value: z.string().min(1) });
const dateRangeFilterSchema = z.looseObject({ range: z.looseObject({}), customValues: z.looseObject({}).optional() });
const stringArraySchema = z.array(z.string());
const compareLocationsSchema = z.array(
  z.looseObject({ page: z.number(), ranges: z.array(z.looseObject({ min: z.number(), max: z.number() })) }),
);
const tableViewsSchema = z.record(z.string(), z.number().nullable());
const recentlyViewedSchema = z.record(
  z.string(),
  z.array(z.looseObject({ title: z.string(), id: z.number(), url: z.string(), entityProp: z.string() })),
);

const yesNo = z.union([z.literal(0), z.literal(1)]).optional();
const inboxExtraFiltersSchema = z.looseObject({ attached: yesNo, any_change: yesNo, scheduled: yesNo, curated: yesNo });
const closedFilter = z.union([z.literal(0), z.literal(1), z.literal(AttachmentParamType.None), z.literal('')]).optional();
const calendarExtraFiltersSchema = z.looseObject({ table_closed: closedFilter, issue_closed: closedFilter });
// `NO_FILTER` is stored as-is when every type is deselected; the object form mirrors
// `SearchItemFilterTypeValidation`'s own schema, which owns the query-param spelling.
const searchTypeFilterSchema = z.union([z.looseObject({ attached: yesNo, scheduled: yesNo, curated: yesNo }), z.literal(NO_FILTER)]);

// Mirrors angular-calendar's `CalendarView`, imported as a type only so the library
// stays out of the initial bundle.
const calendarViewSchema = z.enum(['month', 'week', 'day']);
const issueDetailTabSchema = z.enum(IssueDetailTab);
const sourceViewSchema = z.enum(['tree', 'picker']);

// `user` drives permissions and feature flags, so those are the fields worth guarding;
// the rest of the (large, generated) shape rides along as passthrough.
const userSchema = z.looseObject({
  id: z.number(),
  url: z.string(),
  locale: z.string(),
  company: z.looseObject({
    id: z.number(),
    feature_flags: z.array(z.looseObject({ feature_flag_type_name: z.string() })),
  }),
});

// The encryption key is served fresh with every session and must never reach disk.
const userCodec = (): StorageCodec<User> => {
  const persisted = zodRequired<User>(userSchema, 'user');
  return {
    read: parsed => persisted.read(parsed),
    write: user => {
      const seed = structuredClone(user);
      delete seed.frontend_localstorage_key;
      return persisted.write(seed);
    },
  };
};

// --- schema ----------------------------------------------------------------

// `never` write-parameter keeps the map assignable to a common supertype while
// preserving each entry's concrete read/write types for the derived helpers.
export const STORAGE_SCHEMA = {
  token: { item: LocalStorageItem.AccessToken, isGlobal: true, codec: strOrNull() },
  returnUrl: { item: LocalStorageItem.ReturnUrl, isGlobal: true, codec: str() },
  authTime: { item: LocalStorageItem.AuthTime, isGlobal: true, codec: str() },

  lastSearchIssuesSortOptions: { item: LocalStorageItem.IssueSearchSortOption, codec: str() },
  lastSearchTicketsSortOptions: { item: LocalStorageItem.TicketSearchSortOption, codec: str() },
  lastIssuesSortOptions: { item: LocalStorageItem.IssueSortOption, codec: str() },
  lastTicketsSortOptions: { item: LocalStorageItem.TicketSortOption, codec: str() },
  lastTaskSortOptions: { item: LocalStorageItem.TaskSortOption, codec: str() },
  lastTableSortOptions: { item: LocalStorageItem.TableSortOption, codec: str() },
  lastTemplatesSortOptions: { item: LocalStorageItem.TemplateSortOption, codec: str() },
  lastReportSortOptions: { item: LocalStorageItem.ReportSortOption, codec: str() },
  lastProjectSortOptions: { item: LocalStorageItem.ProjectSortOption, codec: str() },
  lastProcessSortOptions: { item: LocalStorageItem.ProcessSortOption, codec: str() },
  lastSearchProcessSortOptions: { item: LocalStorageItem.SearchProcessSortOption, codec: str() },
  lastTopicSortOptions: { item: LocalStorageItem.TopicSortOption, codec: str() },
  lastReportIssueSortOptions: { item: LocalStorageItem.ReportIssueSortOption, codec: str() },
  tableUserMention: { item: LocalStorageItem.TableUserMention, codec: str() },
  attachTableWithoutFilling: { item: LocalStorageItem.TableAttachWithoutFilling, codec: str() },
  searchActiveTab: { item: LocalStorageItem.WebSearchTab, codec: str() },
  sourceView: { item: LocalStorageItem.SourceView, codec: zodCoerceAs<'tree' | 'picker'>(sourceViewSchema) },

  monitoringSortOption: { item: LocalStorageItem.MonitoringSortOption, codec: strEmptyAsNull() },
  topicInboxSortOption: { item: LocalStorageItem.TopicInboxSortOption, codec: strEmptyAsNull() },

  sidebarState: { item: LocalStorageItem.SidebarState, codec: strOrNull() },
  secondarySidebarState: { item: LocalStorageItem.SecondarySidebarState, codec: strOrNull() },
  areInternalItemSidesSwapped: { item: LocalStorageItem.AreInternalItemSidesSwapped, codec: strOrNull() },
  boardPageByDefault: { item: LocalStorageItem.BoardPageByDefault, codec: strOrNull() },
  searchTreeVisibility: { item: LocalStorageItem.SearchTreeVisibility, codec: strOrNull() },
  hideWeekendCalendar: { item: LocalStorageItem.HideWeekendCalendar, codec: strOrNull() },
  showCompletedTasks: { item: LocalStorageItem.ShowCompletedTasks, codec: strOrNull() },

  appVersion: { item: LocalStorageItem.AppVersion, codec: intOrNull() },
  dashboardTabIndex: { item: LocalStorageItem.DashboardTabIndex, codec: intOrNull() },

  webSearchSorting: { item: LocalStorageItem.WebSearchSorting, codec: coerce() },
  lastFilterTreeIds: { item: LocalStorageItem.WebSearchTreeIds, codec: coerce() },
  lastUserTypeInvite: { item: LocalStorageItem.InviteUserType, codec: coerce() },
  lastLocaleInvite: { item: LocalStorageItem.InviteLocale, codec: coerce() },
  lastCalendarView: { item: LocalStorageItem.StickyCalendarView, codec: zodCoerceAs<CalendarView>(calendarViewSchema) },
  activeIssueTab: { item: LocalStorageItem.ActiveIssueTab, codec: zodCoerceAs<IssueDetailTab>(issueDetailTabSchema) },
  activeTicketTab: { item: LocalStorageItem.ActiveTicketTab, codec: zodCoerceAs<IssueDetailTab>(issueDetailTabSchema) },

  webSearchTypeFilter: {
    item: LocalStorageItem.WebSearchTypeJSONFilter,
    codec: zodCodec<SearchItemFilterType | typeof NO_FILTER | undefined>(searchTypeFilterSchema, () => undefined),
  },
  webSearchLanguageFilter: {
    item: LocalStorageItem.WebSearchLanguageFilter,
    codec: zodCodec<ContentLanguageItem | undefined>(languageFilterSchema, () => undefined),
  },
  webSearchRangeFilter: {
    item: LocalStorageItem.WebSearchRangeFilter,
    codec: zodCodec<IStoredDateRangeFilter | undefined>(dateRangeFilterSchema, () => undefined),
  },
  commonListRangeFilter: {
    item: LocalStorageItem.CommonListRangeFilter,
    codec: zodCodec<IStoredDateRangeFilter | undefined>(dateRangeFilterSchema, () => undefined),
  },

  lastVisitedTableViews: {
    item: LocalStorageItem.LastVisitedTableViews,
    codec: zodCodec<Record<string, FilteredTableId | null>>(tableViewsSchema, () => ({})),
  },
  compareChanges: {
    item: LocalStorageItem.CompareChanges,
    isGlobal: true,
    codec: zodCodec<LariDiffResult['locations'] | null>(compareLocationsSchema, () => null),
  },
  inboxExtraFilters: {
    item: LocalStorageItem.InboxExtraFilters,
    codec: zodCodec<IInboxExtraFilters | undefined>(inboxExtraFiltersSchema, () => undefined),
  },
  calendarExtraFilters: {
    item: LocalStorageItem.CalendarExtraFilters,
    codec: zodCodec<ICalendarExtraFilters>(calendarExtraFiltersSchema, () => ({})),
  },
  listOfStickyBoards: { item: LocalStorageItem.ListOfStickyBoards, codec: zodCodec<string[]>(stringArraySchema, () => []) },
  expandedNodes: { item: LocalStorageItem.WebSearchExpandedTreeNodes, codec: zodCodec<string[]>(stringArraySchema, () => []) },
  recentlyViewed: {
    item: LocalStorageItem.RecentlyViewed,
    codec: zodCodec<IRecentlyViewedStorageItem>(recentlyViewedSchema, () => ({})),
  },
  userTableColumnOrderState: {
    item: LocalStorageItem.UserTableColumnOrderState,
    codec: zodCodec<UserTableColumnOrderState | undefined>(stringArraySchema, () => undefined),
  },
  exportSettings: {
    item: LocalStorageItem.ExportSettings,
    codec: zodCodec<{ layout: ExportLayout; export_attachments: boolean; export_lari_items: boolean } | undefined>(
      exportSettingsSchema,
      () => undefined,
    ),
  },

  commonFilterAssigneeOrOwner: {
    item: LocalStorageItem.CommonFilterAssigneeOrOwner,
    codec: {
      read: (parsed): Filter<UserId> => {
        const value = asString(parsed);
        return value === NO_FILTER ? value : brandedId<UserId>(+value);
      },
      write: (value: Filter<UserId>) => String(value),
    },
  },
  commonFilterProject: {
    item: LocalStorageItem.CommonFilterProject,
    codec: {
      read: (parsed): Filter<ProjectId> => {
        const value = asString(parsed);
        if (!value) {
          return NO_FILTER;
        }
        return value === NO_FILTER ? value : brandedId<ProjectId>(+value);
      },
      write: (value: Filter<ProjectId>) => String(value),
    },
  },

  /**
   * `user` strips its encryption key and re-derives the date-fns locale on write;
   * those side effects live in the accessor, so the codec only handles shape.
   */
  user: { item: LocalStorageItem.User, isGlobal: true, codec: userCodec() },
} satisfies Record<string, StorageEntry<unknown, never>>;

type StorageSchema = typeof STORAGE_SCHEMA;
export type StorageKey = keyof StorageSchema;

export type StorageValue<K extends StorageKey> = StorageSchema[K]['codec'] extends StorageCodec<infer R, infer _W> ? R : never;
export type StorageWriteValue<K extends StorageKey> = StorageSchema[K]['codec'] extends StorageCodec<infer _R, infer W> ? W : never;
